import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Lock, Mail, Visibility, VisibilityOff } from "@mui/icons-material";

import AuthIntegrationStrip from "./components/AuthIntegrationStrip.jsx";
import AuthTextField from "./components/AuthTextField.jsx";
import logoLogin123Zap from "../../shared/assets/images/logo-login-123zap.png";
import { purple } from "../../shared/theme/themePrimitives";
import { ChangePassword } from "../../services/api/Auth/ChangePassword";
import { TryRecovery } from "../../services/api/Auth/TryRecovery";
import { notify } from "../../shared/utils/utils";

const supportUrl =
  "https://wa.me/551151042151?text=Ol%C3%A1%2C%20estou%20precisando%20de%20suporte!%20";

export default function PasswordRecoveryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");

  const [username, setUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mode, setMode] = useState(code ? "checking-token" : "request");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!code) return undefined;

    let active = true;

    async function verifyRecoveryToken() {
      setLoading(true);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_AWS_URL}/auth/verify-token`,
          {
            method: "POST",
            body: JSON.stringify({ token: code }),
          },
        );
        const data = await response.json();

        if (!active) return;

        if (data.status === "success") {
          setMode("change-password");
          return;
        }

        notify("Error", "Erro ao verificar o token", true);
        navigate("/login");
      } catch (error) {
        console.error("Erro ao verificar token de recuperação:", error);
        if (active) {
          setMessage("Não foi possível verificar o link de recuperação.");
          setMode("request");
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    verifyRecoveryToken();

    return () => {
      active = false;
    };
  }, [code, navigate]);

  async function requestRecovery(event) {
    event.preventDefault();

    if (!username) {
      setMessage("Por favor, preencha o campo de e-mail ou usuário.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await TryRecovery({ user: username });

      if (response.status === "success") {
        notify(
          "Success",
          "Enviamos um e-mail para finalizar o processo de recuperação de senha.",
          true,
        );
        navigate("/login");
        return;
      }

      if (response.status === 404) {
        setMessage("Esse e-mail ou usuário é inválido.");
        return;
      }

      setMessage("Não foi possível solicitar a recuperação de senha.");
    } catch (error) {
      console.error("Erro ao solicitar recuperação:", error);
      setMessage("Não foi possível solicitar a recuperação de senha.");
    } finally {
      setLoading(false);
    }
  }

  async function changePassword(event) {
    event.preventDefault();

    if (!newPassword || !confirmPassword) {
      setMessage("Por favor, preencha todos os campos.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("As senhas não coincidem.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await ChangePassword({
        password: newPassword,
        confirmPassword,
        token: code,
      });

      if (response.status === "success") {
        navigate("/login", { state: { message: "Senha alterada com sucesso." } });
        return;
      }

      setMessage("Ocorreu um erro ao alterar a senha.");
    } catch (error) {
      console.error("Erro ao alterar senha:", error);
      setMessage("Ocorreu um erro ao alterar a senha.");
    } finally {
      setLoading(false);
    }
  }

  const isChangingPassword = mode === "change-password";
  const usernameError = Boolean(message) && !isChangingPassword;
  const newPasswordError = Boolean(message) && isChangingPassword && !newPassword;
  const confirmPasswordError =
    Boolean(message) &&
    isChangingPassword &&
    (!confirmPassword || message === "As senhas não coincidem.");

  return (
    <AuthShell>
      {mode === "checking-token" ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={isChangingPassword ? changePassword : requestRecovery}
          sx={{ width: "100%" }}
        >
          <Box>
            <Typography sx={titleSx}>
              {isChangingPassword ? "Crie uma nova senha" : "Recupere sua senha"}
            </Typography>
            <Typography sx={subtitleSx}>
              {isChangingPassword
                ? "Use uma senha forte para voltar a acessar sua conta."
                : "Informe seu e-mail ou usuário para receber o link de recuperação."}
            </Typography>
          </Box>

          {isChangingPassword ? (
            <>
              <AuthTextField
                label="Nova senha"
                required
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                autoFocus
                error={newPasswordError}
                helperText={newPasswordError ? message : undefined}
                icon={<Lock />}
                endAdornment={
                  <PasswordToggle
                    visible={showNewPassword}
                    onClick={() => setShowNewPassword((current) => !current)}
                  />
                }
              />
              <AuthTextField
                label="Confirmar senha"
                required
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                error={confirmPasswordError}
                helperText={confirmPasswordError ? message : undefined}
                icon={<Lock />}
                endAdornment={
                  <PasswordToggle
                    visible={showConfirmPassword}
                    onClick={() => setShowConfirmPassword((current) => !current)}
                  />
                }
              />
              <Button type="submit" variant="contained" fullWidth disabled={loading} sx={buttonSx}>
                Continuar
              </Button>
            </>
          ) : (
            <>
              <AuthTextField
                label="E-mail ou usuário"
                required
                name="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                autoFocus
                error={usernameError}
                helperText={usernameError ? message : undefined}
                icon={<Mail />}
              />
              <Button type="submit" variant="contained" fullWidth disabled={loading} sx={buttonSx}>
                Solicitar recuperação
              </Button>
              <Button
                type="button"
                variant="outlined"
                fullWidth
                disabled={loading}
                onClick={() => window.open(supportUrl, "_blank")}
                sx={outlineButtonSx}
              >
                Preciso de ajuda
              </Button>
              <Typography
                component={Link}
                to="/login"
                sx={{
                  color: purple[600],
                  display: "block",
                  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                  mt: 1.5,
                  textAlign: "center",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Entrar na minha conta
              </Typography>
            </>
          )}
        </Box>
      )}
    </AuthShell>
  );
}

function AuthShell({ children }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(430px, 1fr) minmax(420px, 1fr)",
        },
        minHeight: "100dvh",
        width: "100%",
        overflowX: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          justifyContent: { xs: "flex-start", sm: "center" },
          alignItems: "center",
          width: "100%",
          minWidth: 0,
          px: { xs: 2, sm: 4, md: 5, lg: 7, xl: 8 },
          py: { xs: 3, sm: 4, md: 5, lg: 6 },
          overflowY: "auto",
          "@media (max-height: 760px)": {
            py: { xs: 2.5, sm: 3, md: 3 },
          },
          "@media (max-height: 680px)": {
            justifyContent: "flex-start",
            py: 2,
          },
          "@media (max-height: 560px)": {
            py: 1,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: {
              xs: "min(320px, calc(100vw - 32px))",
              sm: 320,
              lg: 330,
            },
            mb: { xs: 3, md: 4 },
            "@media (max-height: 760px)": { mb: 2.5 },
            "@media (max-height: 560px)": { mb: 1.25 },
          }}
        >
          <Box
            component="img"
            src={logoLogin123Zap}
            alt="123zap"
            sx={{
              display: "block",
              width: { xs: 68, md: 74 },
              height: "auto",
              "@media (max-height: 760px)": { width: 66 },
              "@media (max-height: 560px)": { width: 56 },
            }}
          />
        </Box>

        <Box
          sx={{
            width: "100%",
            maxWidth: {
              xs: "min(320px, calc(100vw - 32px))",
              sm: 320,
              lg: 330,
            },
            minWidth: 0,
          }}
        >
          {children}
        </Box>
      </Box>

      <AuthBrandPanel />
    </Box>
  );
}

function PasswordToggle({ visible, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={visible ? "Ocultar senha" : "Mostrar senha"}
      onClick={onClick}
      sx={{
        appearance: "none",
        display: "grid",
        placeItems: "center",
        width: 24,
        height: 24,
        mr: 1.5,
        p: 0,
        border: 0,
        borderRadius: 0,
        outline: "none",
        cursor: "pointer",
        bgcolor: "transparent",
        color: "#64748B",
        "&:hover": { bgcolor: "transparent", color: "#334155" },
        "&:focus-visible": { color: purple[600] },
        "& svg": { width: 18, height: 18 },
      }}
    >
      {visible ? <VisibilityOff /> : <Visibility />}
    </Box>
  );
}

function AuthBrandPanel() {
  return (
    <Box
      sx={{
        position: "relative",
        display: { xs: "none", md: "flex" },
        alignItems: "center",
        minHeight: "100dvh",
        overflow: "hidden",
        px: { md: 4, lg: 8, xl: 14 },
        py: { md: 5, lg: 8 },
        color: "#fff",
        bgcolor: "#1e293b",
        "@media (max-height: 760px)": {
          px: { md: 4, lg: 7 },
          py: 4,
        },
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 960 540"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMax slice"
        sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      >
        <Box component="g" sx={{ fill: "none", stroke: "#334155", strokeWidth: 100, opacity: 0.25 }}>
          <circle r="234" cx="196" cy="23" />
          <circle r="234" cx="790" cy="491" />
        </Box>
      </Box>

      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 672 }}>
        <Typography sx={brandTitleSx}>Lembre seus clientes pelo WhatsApp</Typography>
        <Typography sx={brandSubtitleSx}>
          Automatize lembretes, avisos e cobranças no momento certo, usando os
          dados do seu sistema para reduzir tarefas manuais e melhorar o
          acompanhamento dos seus clientes.
        </Typography>
        <AuthIntegrationStrip />
      </Box>
    </Box>
  );
}

const titleSx = {
  color: "#141827",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontSize: { xs: 28, sm: 30, lg: 32 },
  fontWeight: 800,
  lineHeight: 1.25,
  "@media (max-height: 760px)": { fontSize: 28 },
  "@media (max-height: 560px)": { fontSize: 26 },
};

const subtitleSx = {
  mt: { xs: 3, md: 4 },
  mb: 2,
  color: "#667085",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontSize: { xs: 15, lg: 16 },
  lineHeight: 1.5,
  "@media (max-height: 760px)": {
    mt: 2.5,
    mb: 1.5,
    fontSize: 15,
  },
  "@media (max-height: 560px)": {
    mt: 1.25,
    mb: 1,
    fontSize: 14,
  },
};

const buttonSx = {
  mt: 0.5,
  minHeight: 40,
  px: 3,
  borderRadius: "999px",
  bgcolor: purple[600],
  boxShadow: "none",
  color: "#fff",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontWeight: 500,
  textTransform: "none",
  "&:hover": { bgcolor: purple[700], boxShadow: "none" },
  "&:active": { bgcolor: purple[800] },
  "&.Mui-disabled": { bgcolor: "rgba(79, 70, 229, 0.42)", color: "#fff" },
  "@media (max-height: 560px)": { minHeight: 38 },
};

const outlineButtonSx = {
  mt: 1,
  minHeight: 40,
  borderRadius: "999px",
  borderColor: purple[200],
  color: purple[600],
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontWeight: 500,
  textTransform: "none",
  "&:hover": {
    borderColor: purple[300],
    bgcolor: purple[50],
  },
  "@media (max-height: 560px)": { minHeight: 38 },
};

const brandTitleSx = {
  color: "#f1f5f9",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontSize: { md: 32, lg: 42, xl: 48 },
  fontWeight: 700,
  lineHeight: 1,
  "@media (max-height: 760px)": { fontSize: { md: 30, lg: 38 } },
};

const brandSubtitleSx = {
  mt: 3,
  color: "#94a3b8",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontSize: { md: 14, lg: 15, xl: 16 },
  lineHeight: 1.5,
  maxWidth: 672,
  "@media (max-height: 760px)": { mt: 2, fontSize: 14 },
};
